import { APIMain } from "@/apiRequester";
import { ApiConstant } from "@/const";
import { IBankRow } from "@/features/banks/types/bank.type";
import { putSysTableAction } from "@/actions/systable-action";
import {
  IUpdateWebUserPayload,
  IWebUserDetail,
} from "@/features/members/types/member.type";
import {
  ICommuneOption,
  ICreateSepayTopupOrderPayload,
  IProfileCreateMemberContext,
  IProfileCreateMemberPayload,
  IProfileDashboard,
  IProfileGeneralSystemSetting,
  IProfileGenerationMembersPage,
  IProfilePagingResult,
  IProfilePointHistoryItem,
  IProfilePurchasePackageContext,
  IProfilePurchasePackagePayload,
  IProfilePurchasePackageResult,
  IProfileRewardHistoryItem,
  IProvinceOption,
  ISepayTopupContext,
  ISepayTopupOrder,
} from "../types/profile.type";

const getDashboard = async () => {
  return APIMain.get<IProfileDashboard>(ApiConstant.PROFILE_API.GET_DASHBOARD);
};

const getPurchasePackageContext = async () => {
  return APIMain.get<IProfilePurchasePackageContext>(
    ApiConstant.PROFILE_API.GET_PURCHASE_PACKAGE_CONTEXT,
  );
};

const purchasePackage = async (payload: IProfilePurchasePackagePayload) => {
  const res = await APIMain.post<IProfilePurchasePackageResult>(
    ApiConstant.PROFILE_API.POST_PURCHASE_PACKAGE,
    payload,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      createdAtUtc: new Date(res.payload.data.createdAtUtc),
    };
  }

  return res;
};

const getGeneralSettings = async () => {
  return APIMain.get<IProfileGeneralSystemSetting>(
    ApiConstant.PROFILE_API.GET_GENERAL_SETTINGS,
  );
};

const getSepayTopupContext = async () => {
  return APIMain.get<ISepayTopupContext>(
    ApiConstant.PROFILE_API.GET_TOPUP_CONTEXT,
  );
};

const mapSepayTopupOrder = (order: ISepayTopupOrder): ISepayTopupOrder => ({
  ...order,
  createdAtUtc: new Date(order.createdAtUtc),
  expiredAtUtc: new Date(order.expiredAtUtc),
  completedAtUtc: order.completedAtUtc ? new Date(order.completedAtUtc) : null,
});

const createSepayTopupOrder = async (
  payload: ICreateSepayTopupOrderPayload,
) => {
  const res = await APIMain.post<ISepayTopupOrder>(
    ApiConstant.PROFILE_API.POST_TOPUP_ORDER,
    payload,
  );

  if (res.payload?.data) {
    res.payload.data = mapSepayTopupOrder(res.payload.data);
  }

  return res;
};

const getSepayTopupOrder = async (orderId: string) => {
  const res = await APIMain.get<ISepayTopupOrder>(
    `${ApiConstant.PROFILE_API.GET_TOPUP_ORDER}/${orderId}`,
    undefined,
    { skipLoading: true },
  );

  if (res.payload?.data) {
    res.payload.data = mapSepayTopupOrder(res.payload.data);
  }

  return res;
};

const getEditInfo = async () => {
  return APIMain.get<IWebUserDetail>(ApiConstant.PROFILE_API.GET_EDIT_INFO);
};

const getBankOptions = async () => {
  return APIMain.get<IBankRow[]>(ApiConstant.PROFILE_API.GET_BANK_OPTIONS);
};

const getProvinceOptions = async () => {
  return APIMain.get<IProvinceOption[]>(
    ApiConstant.PROFILE_API.GET_PROVINCE_OPTIONS,
  );
};

const getCommuneOptions = async (provinceCode: string) => {
  return APIMain.get<ICommuneOption[]>(
    ApiConstant.PROFILE_API.GET_COMMUNE_OPTIONS,
    { provinceCode },
  );
};

const updateEditInfo = async (payload: IUpdateWebUserPayload) => {
  return putSysTableAction(payload, ApiConstant.PROFILE_API.PUT_EDIT_INFO);
};

const getPointHistory = async (params: { skip: number; take: number }) => {
  const res = await APIMain.get<IProfilePagingResult<IProfilePointHistoryItem>>(
    ApiConstant.PROFILE_API.GET_POINT_HISTORY,
    params,
  );

  if (res.payload?.data?.items) {
    res.payload.data.items = res.payload.data.items.map((item) => ({
      ...item,
      createdAtUtc: new Date(item.createdAtUtc),
    }));
  }

  return res;
};

const getRewardHistory = async (params: { skip: number; take: number }) => {
  const res = await APIMain.get<IProfilePagingResult<IProfileRewardHistoryItem>>(
    ApiConstant.PROFILE_API.GET_REWARD_HISTORY,
    params,
  );

  if (res.payload?.data?.items) {
    res.payload.data.items = res.payload.data.items.map((item) => ({
      ...item,
      createdAtUtc: new Date(item.createdAtUtc),
    }));
  }

  return res;
};

const getMembers = async (params: {
  type?: 1 | 2;
  username?: string;
  page: number;
  pageSize: number;
}) => {
  const res = await APIMain.get<IProfileGenerationMembersPage>(
    ApiConstant.PROFILE_API.GET_MEMBERS,
    params,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      members: (res.payload.data.members || []).map((item) => ({
        ...item,
        createdAtUtc: new Date(item.createdAtUtc),
      })),
    };
  }

  return res;
};

const getCreateMemberContext = async () => {
  return APIMain.get<IProfileCreateMemberContext>(
    ApiConstant.PROFILE_API.GET_CREATE_MEMBER_CONTEXT,
  );
};

const createMember = async (payload: IProfileCreateMemberPayload) => {
  return APIMain.post<IWebUserDetail>(
    ApiConstant.PROFILE_API.POST_CREATE_MEMBER,
    payload,
  );
};

export const profileServices = {
  getDashboard,
  getGeneralSettings,
  getPurchasePackageContext,
  purchasePackage,
  getSepayTopupContext,
  createSepayTopupOrder,
  getSepayTopupOrder,
  getEditInfo,
  getBankOptions,
  getProvinceOptions,
  getCommuneOptions,
  updateEditInfo,
  getPointHistory,
  getRewardHistory,
  getMembers,
  getCreateMemberContext,
  createMember,
};
